import React, { useEffect, useRef, useContext } from "react";
import {
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Badge,
  Spinner,
  Button
} from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faInfoCircle, faSync, faClock } from '@fortawesome/free-solid-svg-icons';

import TableDashboardStyle from "../../../assets/css/components/admin/products/TableDashboardStyle.css.js";
import HeadHeaderStyle from "../../../assets/css/components/admin/dashboard/dashboard-head-header/HeaderStyle.css.js";
import { useProviders } from "../../../hooks/api/clientsProvides";
import { GlobalContext } from "../../../contexts/GlobalContext";
import ClientDashboardChart from "./ClientDashboardChart";


export default function DashboardClient() {
  const globalContext = useContext(GlobalContext);
  const { 
    providersData, 
    selectedProviderId, 
    providersDashboardData,
    loading,
    isRefreshing,
    isAutoRefreshing,
    refreshProviders,
    getLastUpdateFormatted,
    forceUpdate,
    fetchProviders
  } = useProviders();
  
  // Referencias para el polling del dashboard
  const intervalRef = useRef(null);
  const hasConfiguredInterval = useRef(false);
  const currentEntIdRef = useRef(globalContext.currentEnterpriseId || "5f1378dcef2f752aeda16673");
  
  useEffect(() => {
    console.log('=== DASHBOARD CLIENT - ESTADO ACTUALIZADO ===');
    console.log('Providers data desde custom hook:', providersData);
    console.log('Provider seleccionado:', selectedProviderId);
    console.log('Dashboard data del provider:', providersDashboardData);
    console.log('Force update:', forceUpdate);
    console.log('Loading:', loading);
    console.log('IsRefreshing:', isRefreshing);
    console.log('IsAutoRefreshing:', isAutoRefreshing);
  }, [providersData, selectedProviderId, providersDashboardData, forceUpdate, loading, isRefreshing, isAutoRefreshing]);

  // Extraer valores para evitar warnings de linting
  const providersDataLength = providersData?.length || 0;
  const hasProvidersData = providersData && providersDataLength > 0;
  const currentEnterpriseId = globalContext.currentEnterpriseId;

  // Efecto para configurar la actualización periódica SOLO UNA VEZ en el dashboard
  useEffect(() => {
    // Actualizar el entId si está disponible en el contexto
    if (currentEnterpriseId) {
      currentEntIdRef.current = currentEnterpriseId;
    }
    
    // Solo configurar si no se ha configurado antes Y hay datos
    if (!hasConfiguredInterval.current && hasProvidersData) {
      console.log('=== CONFIGURANDO ACTUALIZACIÓN PERIÓDICA (DASHBOARD) ===');
      hasConfiguredInterval.current = true;
      
      intervalRef.current = setInterval(() => {
        console.log('=== EJECUTANDO ACTUALIZACIÓN PERIÓDICA (DASHBOARD) ===');
        fetchProviders(currentEntIdRef.current, false, true);
      }, 2 * 60 * 1000); // 2 minutos en milisegundos
    }

    // Cleanup al desmontar componente o cuando cambian las dependencias
    return () => {
      if (intervalRef.current) {
        console.log('=== LIMPIANDO INTERVAL (DASHBOARD) ===');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        hasConfiguredInterval.current = false;
      }
    };
  }, [providersDataLength, hasProvidersData, fetchProviders, currentEnterpriseId]); // Dependencias necesarias

  // Formatear la fecha de última actualización
  const formatLastUpdate = (lastUpdateDate) => {
    if (!lastUpdateDate) return 'No disponible';
    
    const date = new Date(lastUpdateDate);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Caracas'
    });
  };

  // Manejar actualización manual desde el dashboard
  const handleDashboardRefresh = () => {
    console.log('=== MANUAL REFRESH INICIADO ===');
    refreshProviders();
  };

  // Determinar si se está cargando (manual o automático)
  const isLoading = isRefreshing || isAutoRefreshing;

  return (
    <TableDashboardStyle>
      <Container fluid>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 text-muted">Cargando datos del dashboard...</p>
          </div>
        ) : (
          <>
            <Row>
              <Col xs={12}>
                <div className="modern-header">
                  <div className="header-content">
                    {/* Sección principal del header */}
                    <div className="header-main">
                      <div className="welcome-section">
                        <span className="brand-text">Bienvenido a <strong>E-kommers</strong></span>
                        <h1 className="main-title">
                          Dashboard de Inventario
                          {providersDashboardData && (
                            <span className="provider-name"> - {providersDashboardData.ownerName}</span>
                          )}
                        </h1>
                        <p className="subtitle">
                          Panel de control integral para el seguimiento de su inventario
                        </p>
                      </div>

                      {/* Información de actualización destacada - Esquina superior derecha */}
                      {providersDashboardData?.lastUpdate && (
                        <div className="update-info-card">
                          <div className="update-info-content">
                            <div className="update-icon">
                              <FontAwesomeIcon icon={faCalendarAlt} />
                            </div>
                            <div className="update-details">
                              <div className="update-title">Última Actualización</div>
                              <div className="update-date">
                                {formatLastUpdate(providersDashboardData.lastUpdate)}
                              </div>
                            </div>
                            <div className="update-actions">
                              <OverlayTrigger
                                placement="bottom"
                                overlay={
                                  <Tooltip>
                                    <div>
                                      <strong>Refrescar Vista</strong>
                                      <div className="mt-1 small">Auto-refresco cada 2 minutos</div>
                                    </div>
                                  </Tooltip>
                                }
                              >
                                <Button
                                  variant="light"
                                  size="sm"
                                  onClick={handleDashboardRefresh}
                                  disabled={isLoading}
                                  className="refresh-btn-compact"
                                >
                                  <FontAwesomeIcon 
                                    icon={faSync} 
                                    spin={isLoading}
                                  />
                                </Button>
                              </OverlayTrigger>
                            </div>
                          </div>
                          <div className="auto-refresh-status">
                            <div className="auto-refresh-indicator active"></div>
                            <span className="auto-refresh-text">
                              {isLoading ? (
                                <span className="d-flex align-items-center">
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  {isRefreshing ? 'Actualizando manualmente...' : 'Actualizando automáticamente...'}
                                </span>
                              ) : (
                                'Auto-refresco activo'
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            
            {/* Dashboard principal */}
            <Row>
              <Col xs={12}>
                <ClientDashboardChart key={`dashboard-chart-${forceUpdate}`} />
              </Col>
            </Row>
          </>
        )}
      </Container>
      
      {/* CSS mejorado */}
      <style jsx>{`
        .modern-header {
          background: linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(0,0,0,.05);
          box-shadow: 0 4px 20px rgba(0,0,0,.05);
        }

        .header-content {
          width: 100%;
        }

        .header-main {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .welcome-section {
          flex: 1;
        }

        .brand-text {
          color: #6c757d;
          font-size: 0.9rem;
          font-weight: 400;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
          display: block;
        }

        .main-title {
          color: #2c3e50;
          font-size: 2.25rem;
          font-weight: 700;
          margin: 0.5rem 0;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }

        .provider-name {
          color: #3b82f6;
          font-weight: 600;
        }

        .subtitle {
          color: #64748b;
          font-size: 1rem;
          font-weight: 400;
          margin: 0;
          letter-spacing: 0.01em;
        }

        .update-info-card {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 12px;
          padding: 1rem;
          color: white;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.25);
          border: 1px solid rgba(255, 255, 255, 0.1);
          min-width: 280px;
          max-width: 320px;
        }

        .update-info-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .update-icon {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          backdrop-filter: blur(10px);
          flex-shrink: 0;
        }

        .update-details {
          flex: 1;
          min-width: 0;
        }

        .update-title {
          font-size: 0.75rem;
          opacity: 0.9;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }

        .update-date {
          font-size: 0.9rem;
          font-weight: 600;
          color: #ffffff;
          line-height: 1.2;
        }

        .update-actions {
          flex-shrink: 0;
        }

        .refresh-btn-compact {
          border-radius: 8px;
          padding: 0.5rem;
          font-weight: 500;
          transition: all 0.2s ease;
          border: none;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          backdrop-filter: blur(10px);
          min-width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .refresh-btn-compact:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .refresh-btn-compact:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auto-refresh-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          backdrop-filter: blur(10px);
        }

        .auto-refresh-indicator {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #10b981;
          animation: pulse 2s infinite;
        }

        .auto-refresh-text {
          font-size: 0.75rem;
          font-weight: 500;
          color: #ffffff;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .header-main {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .update-info-card {
            align-self: flex-end;
            max-width: none;
            width: 100%;
          }
          
          .main-title {
            font-size: 1.875rem;
          }
        }

        @media (max-width: 768px) {
          .modern-header {
            padding: 1.5rem;
          }
          
          .main-title {
            font-size: 1.5rem;
          }
          
          .update-info-card {
            padding: 0.75rem;
            min-width: 250px;
          }
          
          .update-info-content {
            gap: 0.5rem;
          }
          
          .update-icon {
            padding: 0.4rem;
            font-size: 0.9rem;
          }
          
          .update-date {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 576px) {
          .modern-header {
            padding: 1rem;
          }
          
          .main-title {
            font-size: 1.25rem;
          }
          
          .brand-text {
            font-size: 0.8rem;
          }
          
          .subtitle {
            font-size: 0.9rem;
          }
          
          .update-info-card {
            padding: 0.75rem;
            min-width: 220px;
          }
          
          .update-date {
            font-size: 0.8rem;
          }
          
          .refresh-btn-compact {
            min-width: 32px;
            height: 32px;
            padding: 0.4rem;
          }
        }
      `}</style>
    </TableDashboardStyle>
  );
}
